import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { SignPlayer } from "../../components/SignPlayer";
import type { SignWord } from "../../components/SignPlayer";
import {
  SIGN_CONVERSION_STAGE_LABELS,
  useSignWordsWs,
} from "../../hooks/use-sign-words-ws";
import { apiFetch } from "../../lib/api-client";

const searchSchema = z.object({
  text: z.string().optional(),
  kanjiText: z.string().optional(),
  id: z.string().optional(),
});

export const Route = createFileRoute("/_app/dictation_/sign")({
  component: SignPlayerPage,
  validateSearch: searchSchema,
});

type SignItemResponse = {
  id: string;
  text: string;
  words: SignWord[];
};

const fetchSignItem = (id: string): Promise<SignItemResponse> =>
  apiFetch<SignItemResponse>(`/sign-items/${encodeURIComponent(id)}`);

function SignPlayerPage() {
  const { text, kanjiText, id } = useSearch({ from: "/_app/dictation_/sign" });

  const { state: wsState, convert } = useSignWordsWs();
  const itemQuery = useQuery({
    queryKey: ["sign-item", id],
    queryFn: () => fetchSignItem(id as string),
    enabled: !!id,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: convert は初回のみ発火させたいため text/kanjiText のみを依存に持つ
  useEffect(() => {
    if (text && !id) {
      convert({ text, ...(kanjiText ? { kanjiText } : {}) });
    }
  }, [text, kanjiText, id]);

  const result = id
    ? itemQuery.data
    : wsState.status === "done"
      ? { text: wsState.text, words: wsState.words }
      : undefined;
  const isLoading = id
    ? itemQuery.isLoading
    : wsState.status === "connecting" || wsState.status === "progress";
  const isError = id ? itemQuery.isError : wsState.status === "error";
  const errorMessage = id
    ? (itemQuery.error as Error | null)?.message
    : wsState.status === "error"
      ? wsState.message
      : undefined;
  const loadingLabel = id
    ? "読み込み中..."
    : wsState.status === "progress"
      ? SIGN_CONVERSION_STAGE_LABELS[wsState.stage]
      : "接続しています...";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/dictation"
          className="text-sm text-neutral-500 hover:text-neutral-700"
        >
          ← もどる
        </Link>
      </div>

      {result && <p className="text-sm text-neutral-600">{result.text}</p>}

      {isLoading && <LoadingIndicator label={loadingLabel} variant="bar" />}

      {isError && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {id
            ? `手話データの読み込みに失敗しました: ${errorMessage}`
            : errorMessage}
          {!id && text && (
            <button
              type="button"
              onClick={() =>
                convert({ text, ...(kanjiText ? { kanjiText } : {}) })
              }
              className="ml-2 underline"
            >
              もう一度試す
            </button>
          )}
        </div>
      )}

      {result && <SignPlayer words={result.words} />}
    </div>
  );
}
